import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { rupiah, shortDate, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";

const EMPTY = { date: todayISO(), vendor: "", category: "Operasional", amount: 0, notes: "" };

export default function Accounting() {
  const [summary, setSummary] = useState({ income: 0, expense: 0, profit: 0 });
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [f, setF] = useState(EMPTY);

  const load = async () => {
    const [s, e, i] = await Promise.all([
      api.get("/accounting/summary"),
      api.get("/expenses"),
      api.get("/invoices"),
    ]);
    setSummary(s.data); setExpenses(e.data); setInvoices(i.data);
  };
  useEffect(() => { load(); }, []);

  const addExpense = async () => {
    if (!f.vendor || !f.amount) return toast.error("Lengkapi vendor & nominal");
    await api.post("/expenses", { ...f, amount: Number(f.amount) });
    setF(EMPTY); toast.success("Expense ditambahkan"); load();
  };

  const del = async (id) => {
    if (!window.confirm("Hapus expense?")) return;
    await api.delete(`/expenses/${id}`); load();
  };

  const Stat = ({ label, value, color }) => (
    <div className="stat-card">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-extrabold mono mt-2 ${color}`}>{rupiah(value)}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Accounting</h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Income, expense, dan net profit.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <Stat label="Income" value={summary.income} color="text-[#6b8e23]" />
        <Stat label="Expense" value={summary.expense} color="text-red-600" />
        <Stat label="Net Profit" value={summary.profit} color="text-[#556b2f]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Income (Invoice Paid)</CardTitle></CardHeader>
          <CardContent>
            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {invoices.map(i => (
                <div key={i.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="mono font-bold text-sm">{i.invoice_number}</div>
                      <div className="text-sm truncate">{i.customer_name}</div>
                      <div className="text-xs text-slate-500">{shortDate(i.invoice_date)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="mono font-bold text-sm">{rupiah(i.total)}</div>
                      <span className={`pill mt-1 inline-block ${i.payment_status === "Paid" ? "pill-paid" : "pill-unpaid"}`}>{i.payment_status}</span>
                    </div>
                  </div>
                </div>
              ))}
              {invoices.length === 0 && <div className="text-center text-slate-400 py-8">Belum ada invoice.</div>}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
            <Table className="min-w-[600px]">
              <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Tanggal</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {invoices.map(i => (
                  <TableRow key={i.id}>
                    <TableCell className="mono">{i.invoice_number}</TableCell>
                    <TableCell>{shortDate(i.invoice_date)}</TableCell>
                    <TableCell>{i.customer_name}</TableCell>
                    <TableCell className="text-right mono">{rupiah(i.total)}</TableCell>
                    <TableCell><span className={`pill ${i.payment_status === "Paid" ? "pill-paid" : "pill-unpaid"}`}>{i.payment_status}</span></TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Belum ada invoice.</TableCell></TableRow>}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Expense</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="exp-date"/></div>
              <div><Label>Vendor</Label><Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} data-testid="exp-vendor"/></div>
              <div>
                <Label>Kategori</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="exp-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["Refund","Transfer","Admin Bank","Domain","Hosting","Operasional","Marketing","Gaji","Lainnya"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nominal</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="exp-amount"/></div>
              <div className="sm:col-span-2"><Label>Catatan</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>
              <div className="sm:col-span-2"><Button onClick={addExpense} className="bg-[#556b2f] hover:bg-[#3d4d1e] w-full" data-testid="exp-add-btn"><Plus size={14} className="mr-1"/> Tambah Expense</Button></div>
            </div>

            <div className="overflow-x-auto -mx-6 md:mx-0">
            <Table className="min-w-[500px]">
              <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Vendor</TableHead><TableHead>Kategori</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {expenses.map(e => (
                  <TableRow key={e.id}>
                    <TableCell>{shortDate(e.date)}</TableCell>
                    <TableCell>{e.vendor}</TableCell>
                    <TableCell>{e.category}</TableCell>
                    <TableCell className="text-right mono">{rupiah(e.amount)}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => del(e.id)}><Trash2 size={14} className="text-red-600"/></Button></TableCell>
                  </TableRow>
                ))}
                {expenses.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Belum ada expense.</TableCell></TableRow>}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
